export const getStatusDisplay = (
  connectionStatus: "VALID" | "INVALID" | "UNKNOWN" | string,
): string => {
  if (connectionStatus === "VALID") return "Active";
  if (connectionStatus === "INVALID") return "Failed";
  if (connectionStatus === "UNKNOWN") return "Pending";
  return "Pending";
};

export const formatDate = (timestamp?: number): string => {
  if (!timestamp) return "—";
  const date = new Date(timestamp);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

export const getMetadataValue = (
  metadata: Record<string, unknown> | undefined,
  key: string,
): string => {
  if (!metadata) return "—";
  const value = metadata[key];
  return value ? String(value) : "—";
};

/**
 * Parse cron "minute hour ..." into a 12-hour clock string. Returns "—" if the
 * cron has wildcards or can't be interpreted as a fixed time-of-day.
 */
export const formatCronTime = (cronExpression?: string): string => {
  if (!cronExpression) return "—";
  try {
    const parts = cronExpression.trim().split(/\s+/);
    if (parts.length < 2) return "—";
    const [minuteStr, hourStr] = parts;
    if (minuteStr === "*" || hourStr === "*") return "—";
    const minute = parseInt(minuteStr, 10);
    const hour = parseInt(hourStr, 10);
    if (
      Number.isNaN(hour) ||
      Number.isNaN(minute) ||
      hour < 0 ||
      hour > 23 ||
      minute < 0 ||
      minute > 59
    ) {
      return "—";
    }
    const time = new Date();
    time.setHours(hour, minute, 0, 0);
    return time.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  } catch {
    return "—";
  }
};

export const formatScheduleWithTime = (
  scheduleName: string,
  cronExpression?: string,
): string => {
  const timeStr = formatCronTime(cronExpression);
  if (timeStr === "—") return scheduleName;
  return `${scheduleName} at ${timeStr}`;
};

export const formatImportTime = (cronExpression?: string): string => {
  if (!cronExpression) return "—";
  try {
    const parts = cronExpression.trim().split(/\s+/);
    if (parts.length < 2) return "—";
    const [, hourStr] = parts;

    // "0 * * * *" — wildcard hour means every hour
    if (hourStr === "*") {
      return "Every 1 hour";
    }

    // "0 */N * * *" — every N hours
    if (hourStr.startsWith("*/")) {
      const interval = parseInt(hourStr.slice(2), 10);
      if (!isNaN(interval) && interval > 0) {
        return `Every ${interval} hour${interval === 1 ? "" : "s"}`;
      }
    }

    // Fixed hour and minute — daily at a specific time
    const timeStr = formatCronTime(cronExpression);
    if (timeStr !== "—") {
      return `Daily at ${timeStr}`;
    }
  } catch {
    // fall through
  }
  return "—";
};

interface SchedulableFeature {
  schedules?: Array<{
    scheduleName?: string;
    cronExpression?: string;
  }>;
}

export const getScheduleSummary = (
  features: SchedulableFeature[] | undefined,
): { frequency: string; importTime: string } => {
  const schedule = (features ?? [])
    .flatMap((f) => f.schedules ?? [])
    .find((s) => !!s);
  if (!schedule) return { frequency: "—", importTime: "—" };
  return {
    frequency: schedule.scheduleName || "—",
    importTime: formatCronTime(schedule.cronExpression),
  };
};

export const getImportTimeDisplay = (
  features: SchedulableFeature[] | undefined,
): string => {
  const schedule = (features ?? [])
    .flatMap((f) => f.schedules ?? [])
    .find((s) => !!s);
  if (!schedule) return "—";
  return formatImportTime(schedule.cronExpression);
};
